import { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { ChevronLeft, User } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import OnboardingProgress from '@/components/OnboardingProgress';
import DobScrollPicker from '@/components/DobScrollPicker';
import { getMinorStatus, shouldBlockUser } from '@/lib/minorCompliance';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export default function OnboardingQuickProfile() {
  const router = useRouter();
  const { user, profile, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;
  const insets = useSafeAreaInsets();

  const [gender, setGender] = useState<'male' | 'female' | null>(null);
  const [bodyweight, setBodyweight] = useState('');
  const [unit, setUnit] = useState<'lbs' | 'kg'>('lbs');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [dobYear, setDobYear] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const convertToLbs = (weight: string, currentUnit: 'lbs' | 'kg'): number => {
    const num = parseFloat(weight);
    if (isNaN(num)) return 0;
    return currentUnit === 'kg' ? num * 2.20462 : num;
  };

  const getHeightCm = (): number | null => {
    if (unit === 'kg') {
      // Direct cm input stored in heightFt field
      const cm = parseInt(heightFt);
      if (isNaN(cm) || cm <= 0) return null;
      return cm;
    }
    const ft = parseInt(heightFt);
    const inches = parseInt(heightIn) || 0;
    if (isNaN(ft) || ft <= 0) return null;
    return Math.round((ft * 12 + inches) * 2.54);
  };

  const getDob = (): string | null => {
    const m = parseInt(dobMonth);
    const d = parseInt(dobDay);
    const y = parseInt(dobYear);
    if (isNaN(m) || isNaN(d) || isNaN(y)) return null;
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1920 || y > new Date().getFullYear() - 13) return null;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };

  const handleContinue = async () => {
    if (!gender) { setError('Please select your gender'); return; }

    const weightNum = parseFloat(bodyweight.trim());
    if (!bodyweight.trim() || isNaN(weightNum) || weightNum <= 0) {
      setError('Please enter a valid bodyweight'); return;
    }

    const heightCm = getHeightCm();
    if (!heightCm || heightCm < 100 || heightCm > 250) {
      setError('Please enter a valid height'); return;
    }

    const dob = getDob();
    if (!dob) { setError('Please enter a valid date of birth'); return; }

    // Age check: must be at least 13 (exact age calculation)
    const birthDate = new Date(dob);
    const today = new Date();
    const age = Math.floor((today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    if (age < 13 || age > 120) { setError('You must be at least 13 years old'); return; }

    setSaving(true);
    setError('');

    try {
      // Determine minor status from birth year for routing
      const birthYear = new Date(dob).getFullYear();
      const minorStatus = getMinorStatus(birthYear);

      // Save profile in background (fire-and-forget, navigate first)
      if (user?.id) {
        const bodyweightLbs = convertToLbs(bodyweight, unit);
        void Promise.resolve(supabase.from('profiles').update({
          gender,
          bodyweight: bodyweightLbs,
          units: unit,
          height_cm: heightCm,
          date_of_birth: dob,
          // Phase 2E: also save birth_year for minor compliance checks
          birth_year: birthYear,
        }).eq('id', user.id));
        refreshProfile?.().catch(() => {});
      }

      // Phase 2E: route based on minor status
      if (shouldBlockUser(birthYear)) {
        // Under 13 — hard block, cannot proceed
        router.replace('/(onboarding)/age-blocked');
        setSaving(false);
        return;
      }

      if (minorStatus === 'younger_minor') {
        // Age 13–15 — requires guardian acknowledgment before terms
        router.replace('/(onboarding)/guardian-consent');
        setSaving(false);
        return;
      }

      // adult or older_minor (16+): continue normal flow
      router.replace('/(onboarding)/terms-accept');
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    }

    setSaving(false);
  };

  const isComplete = gender && bodyweight.trim() && heightFt.trim() && dobMonth && dobDay && dobYear;

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <TouchableOpacity style={[styles.backBtn, { top: insets.top + 8 }]} onPress={() => router.back()}>
        <ChevronLeft size={24} color={c.text} />
      </TouchableOpacity>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={styles.inner}>
          <OnboardingProgress step={4} />
          <User size={36} color={c.accent} />
          <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            ABOUT YOU
          </Text>
          <Text style={[styles.subtitle, { color: c.textSecondary }]}>
            This data is used to calculate your DOTS score, weight class, and competitive ranking. Accuracy matters — your numbers compete against real people.
          </Text>
          <View style={{ backgroundColor: c.accent + '12', borderRadius: 2, borderLeftWidth: 3, borderLeftColor: c.accent, padding: 10, marginTop: 4 }}>
            <Text style={{ color: c.accent, fontSize: 12, fontWeight: '700' }}>Why this is required</Text>
            <Text style={{ color: c.textSecondary, fontSize: 12, marginTop: 2, lineHeight: 17 }}>
              Gender and bodyweight determine your weight class and relative strength score (DOTS). Without accurate data, your ranking is meaningless.
            </Text>
          </View>

          {/* Gender */}
          <View style={styles.section}>
            <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>GENDER <Text style={{ color: c.error || '#ff4444', fontSize: 10 }}>REQUIRED</Text></Text>
            <Text style={{ fontSize: 11, color: c.textTertiary, marginBottom: 6 }}>Determines your weight class and competitive division. Cannot be changed after onboarding.</Text>
            <View style={styles.toggleRow}>
              {(['male', 'female'] as const).map((g) => (
                <TouchableOpacity
                  key={g}
                  style={[
                    styles.toggleBtn,
                    {
                      backgroundColor: gender === g ? c.accent : c.surface,
                      borderColor: gender === g ? c.accent : c.border,
                    },
                  ]}
                  onPress={() => { setGender(g); setError(''); }}
                  accessibilityRole="button"
                  accessibilityLabel={g === 'male' ? 'Select male' : 'Select female'}
                  accessibilityState={{ selected: gender === g }}
                >
                  <Text style={[styles.toggleText, {
                    color: gender === g ? c.accentForeground : c.text,
                    fontFamily: TYPO.body.semibold,
                  }]}>
                    {g === 'male' ? 'Male' : 'Female'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Date of Birth */}
          <View style={styles.section} accessibilityLabel="Date of birth picker" accessibilityRole="none">
            <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>DATE OF BIRTH <Text style={{ color: c.error || '#ff4444', fontSize: 10 }}>REQUIRED</Text></Text>
            <Text style={{ fontSize: 11, color: c.textTertiary, marginBottom: 6 }}>Used for age bracket ranking (Junior, Open, Masters 40+).</Text>
            <DobScrollPicker
              month={dobMonth}
              day={dobDay}
              year={dobYear}
              onChangeMonth={(v) => { setDobMonth(v); setError(''); }}
              onChangeDay={(v) => { setDobDay(v); setError(''); }}
              onChangeYear={(v) => { setDobYear(v); setError(''); }}
              colors={{
                text: c.text,
                textSecondary: c.textSecondary,
                textTertiary: c.textTertiary,
                surface: c.surface,
                border: c.border,
                accent: c.accent,
              }}
            />
          </View>

          {/* Height */}
          <View style={styles.section}>
            <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>HEIGHT</Text>
            {unit === 'kg' ? (
              <TextInput
                style={[styles.weightInput, { color: c.text, backgroundColor: c.surface, borderColor: c.border }]}
                value={heightFt}
                onChangeText={(t) => { setHeightFt(t.replace(/[^0-9]/g, '').slice(0, 3)); setError(''); }}
                placeholder="cm"
                placeholderTextColor={c.textTertiary}
                keyboardType="numeric"
                maxLength={3}
                returnKeyType="next"
                accessibilityLabel="Height in centimeters"
              />
            ) : (
              <View style={styles.toggleRow}>
                <TextInput
                  style={[styles.heightInputEqual, { color: c.text, backgroundColor: c.surface, borderColor: c.border }]}
                  value={heightFt}
                  onChangeText={(t) => { setHeightFt(t.replace(/[^0-9]/g, '')); setError(''); }}
                  placeholder="ft"
                  placeholderTextColor={c.textTertiary}
                  keyboardType="numeric"
                  maxLength={1}
                  returnKeyType="next"
                  accessibilityLabel="Height feet"
                />
                <TextInput
                  style={[styles.heightInputEqual, { color: c.text, backgroundColor: c.surface, borderColor: c.border }]}
                  value={heightIn}
                  onChangeText={(t) => { setHeightIn(t.replace(/[^0-9]/g, '')); setError(''); }}
                  placeholder="in"
                  placeholderTextColor={c.textTertiary}
                  keyboardType="numeric"
                  maxLength={2}
                  returnKeyType="next"
                  accessibilityLabel="Height inches"
                />
              </View>
            )}
          </View>

          {/* Units */}
          <View style={styles.section}>
            <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>UNITS</Text>
            <View style={styles.toggleRow}>
              {(['lbs', 'kg'] as const).map((u) => (
                <TouchableOpacity
                  key={u}
                  style={[
                    styles.toggleBtn,
                    {
                      backgroundColor: unit === u ? c.accent : c.surface,
                      borderColor: unit === u ? c.accent : c.border,
                    },
                  ]}
                  onPress={() => {
                    if (u === unit) return;
                    // Convert values instead of clearing
                    const bw = parseFloat(bodyweight);
                    if (!isNaN(bw) && bw > 0) {
                      setBodyweight(u === 'kg' ? (bw / 2.20462).toFixed(1) : (bw * 2.20462).toFixed(1));
                    }
                    // Convert height
                    if (u === 'kg') {
                      // ft/in → cm
                      const ft = parseInt(heightFt);
                      const inches = parseInt(heightIn) || 0;
                      if (!isNaN(ft) && ft > 0) {
                        setHeightFt(String(Math.round((ft * 12 + inches) * 2.54)));
                      } else {
                        setHeightFt('');
                      }
                      setHeightIn('');
                    } else {
                      // cm → ft/in
                      const cm = parseInt(heightFt);
                      if (!isNaN(cm) && cm > 0) {
                        const totalIn = cm / 2.54;
                        setHeightFt(String(Math.floor(totalIn / 12)));
                        setHeightIn(String(Math.round(totalIn % 12)));
                      } else {
                        setHeightFt('');
                        setHeightIn('');
                      }
                    }
                    setUnit(u);
                    setError('');
                  }}
                >
                  <Text style={[styles.toggleText, {
                    color: unit === u ? c.accentForeground : c.text,
                    fontFamily: TYPO.body.semibold,
                  }]}>
                    {u}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Bodyweight */}
          <View style={styles.section}>
            <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>BODYWEIGHT <Text style={{ color: c.error || '#ff4444', fontSize: 10 }}>REQUIRED</Text></Text>
            <Text style={{ fontSize: 11, color: c.textTertiary, marginBottom: 6 }}>Your competition bodyweight. Used for weight class placement and DOTS relative strength scoring. Enter your honest training weight.</Text>
            <TextInput
              style={[styles.weightInput, { color: c.text, backgroundColor: c.surface, borderColor: c.border }]}
              value={bodyweight}
              onChangeText={(text) => { setBodyweight(text.replace(/[^0-9.]/g, '')); setError(''); }}
              placeholder={unit === 'kg' ? '80' : '175'}
              placeholderTextColor={c.textTertiary}
              keyboardType="numeric"
              returnKeyType="next"
              accessibilityLabel="Bodyweight"
            />
          </View>

          {error ? (
            <Text style={[styles.errorText, { color: '#ef4444' }]}>{error}</Text>
          ) : null}
        </View>

        <View style={styles.bottom}>
          <Pressable
            style={[styles.primaryBtn, {
              backgroundColor: c.accent,
              opacity: (!isComplete || saving) ? 0.5 : 1,
            }]}
            onPress={handleContinue}
            disabled={!isComplete || saving}
            android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
            hitSlop={{ top: 8, bottom: 8 }}
            accessibilityRole="button"
          >
            {saving ? (
              <ActivityIndicator size="small" color={c.accentForeground} />
            ) : (
              <Text style={[styles.primaryBtnText, { color: c.accentForeground, fontFamily: TYPO.heading.bold }]}>
                CONTINUE
              </Text>
            )}
          </Pressable>
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: { position: 'absolute', top: 50, left: 16, zIndex: 10, padding: 8 },
  scrollContent: { flexGrow: 1 },
  inner: { flex: 1, paddingHorizontal: 24, paddingTop: 80, alignItems: 'center', gap: 20 },
  title: { fontSize: 28, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1 },
  subtitle: { fontSize: 15, textAlign: 'center', lineHeight: 22, maxWidth: 340, marginBottom: 4 },
  section: { width: '100%', gap: 8 },
  sectionLabel: { fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase' },
  toggleRow: { flexDirection: 'row', gap: 12 },
  toggleBtn: { flex: 1, paddingVertical: 14, borderWidth: 1, borderRadius: 2, alignItems: 'center' },
  toggleText: { fontSize: 16 },
  heightInputEqual: {
    flex: 1, borderWidth: 1, borderRadius: 2, height: 48, fontSize: 18,
    fontWeight: '700', textAlign: 'center', overflow: 'hidden',
  },
  weightInput: {
    width: '100%', borderWidth: 1, borderRadius: 2, paddingHorizontal: 14,
    height: 48, fontSize: 18, fontWeight: '700', textAlign: 'center',
  },
  errorText: { fontSize: 13, textAlign: 'center' },
  bottom: { paddingHorizontal: 24, paddingBottom: 40, paddingTop: 16, alignItems: 'center' },
  primaryBtn: { width: '100%', paddingVertical: 18, borderRadius: 0, alignItems: 'center' },
  primaryBtnText: { fontSize: 18, textTransform: 'uppercase', letterSpacing: 1 },
});

